// src/pages/manager/ManagerDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { useCurrentUser } from '../../context/UserContext';
import { ChartPanel, CompareBar } from '../../components/charts';
import { localizeStudentsByBranch } from '../../utils/chartSeries';

const ManagerDashboard = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useCurrentUser();
    const [coachCount, setCoachCount] = useState(null);
    const [studentCount, setStudentCount] = useState(null);
    const [activeBranchCount, setActiveBranchCount] = useState(null);
    const [studentsByBranch, setStudentsByBranch] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statsLoadError, setStatsLoadError] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setStatsLoadError(false);
            try {
                const { data } = await API.get('/manager/dashboard/stats');
                setCoachCount(data.coachCount ?? 0);
                setStudentCount(data.studentCount ?? 0);
                setActiveBranchCount(data.branchCount ?? 0);
                setStudentsByBranch(localizeStudentsByBranch(
                    Array.isArray(data.studentsByBranch) ? data.studentsByBranch : [],
                    t,
                ));
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setCoachCount('—');
                setStudentCount('—');
                setActiveBranchCount('—');
                setStudentsByBranch([]);
                setStatsLoadError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [t]);

    const MetricCard = ({ title, value, icon, gradient, onClick }) => (
        <div
            className={`bg-gradient-to-br ${gradient} rounded-2xl shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-all transform hover:scale-105`}
            onClick={onClick}
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm opacity-90 mb-1">{title}</div>
                    <div className="text-4xl font-bold">{loading ? '...' : value}</div>
                </div>
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    {icon}
                </div>
            </div>
        </div>
    );

    const QuickActionCard = ({ title, description, icon, onClick, color }) => (
        <button
            onClick={onClick}
            className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-all text-left border border-gray-100 hover:border-gray-200 w-full"
        >
            <div className="flex items-start gap-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    {icon}
                </div>
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                    <p className="text-sm text-gray-600">{description}</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
            </div>
        </button>
    );

    return (
        <div className="page-shell">
            {statsLoadError && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                    {t('pages.managerDashboard.statsLoadFailed', {
                        defaultValue: 'Dashboard stats could not be loaded. Figures below may be incomplete — refresh to try again.',
                    })}
                </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                    {t('dashboard.welcome', { name: user?.firstName || t('dashboard.defaultManager') })}
                </h1>
                <p className="text-gray-600 mt-2">
                    {t('pages.managerDashboard.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                    title={t('pages.managerDashboard.coaches')}
                    value={coachCount !== null ? coachCount : '—'}
                    gradient="from-blue-500 to-blue-600"
                    onClick={() => navigate('/manager/coaches')}
                    icon={
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    }
                />

                <MetricCard
                    title={t('pages.managerDashboard.students')}
                    value={studentCount !== null ? studentCount : '—'}
                    gradient="from-green-500 to-emerald-600"
                    onClick={() => navigate('/manager/students')}
                    icon={
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                    }
                />

                <MetricCard
                    title={t('pages.adminDashboard.activeBranches')}
                    value={activeBranchCount !== null ? activeBranchCount : '—'}
                    gradient="from-purple-500 to-purple-600"
                    onClick={() => navigate('/manager/branches')}
                    icon={
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    }
                />
            </div>

            <ChartPanel
                title={t('charts.studentsByBranch', { defaultValue: 'Students by branch' })}
                subtitle={t('charts.managerBranchHint', { defaultValue: 'Active students across your branches' })}
                actionTo="/manager/students"
                actionLabel={t('charts.viewStudents', { defaultValue: 'Students' })}
                loading={loading}
                empty={!loading && !studentsByBranch.some((r) => Number(r.value) > 0)}
                emptyMessage={
                    Number(studentCount) > 0
                        ? t('charts.noBranchEnrollmentYet', { defaultValue: 'Students are visible but none are enrolled in your branches yet' })
                        : t('charts.noStudentsInYourBranches', { defaultValue: 'No enrolled students in your branches yet' })
                }
                error={statsLoadError ? t('pages.managerDashboard.statsLoadFailed', { defaultValue: 'Stats unavailable' }) : null}
                height={260}
            >
                <CompareBar
                    data={studentsByBranch.slice(0, 10)}
                    height={Math.max(180, studentsByBranch.length * 36)}
                    layout={studentsByBranch.length > 1 ? 'horizontal' : 'vertical'}
                    labelMaxLen={22}
                />
            </ChartPanel>

            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{t('dashboard.quickActions')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <QuickActionCard
                        title={t('pages.managerDashboard.manageBatches')}
                        description={t('pages.managerDashboard.manageBatchesDesc')}
                        color="from-blue-500 to-blue-600"
                        onClick={() => navigate('/manager/batches')}
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        }
                    />

                    <QuickActionCard
                        title={t('pages.adminDashboard.viewSessions')}
                        description={t('pages.adminDashboard.viewSessionsDesc')}
                        color="from-green-500 to-green-600"
                        onClick={() => navigate('/manager/sessions')}
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        }
                    />

                    <QuickActionCard
                        title={t('pages.managerDashboard.enrollments')}
                        description={t('pages.managerDashboard.enrollmentsDesc')}
                        color="from-purple-500 to-purple-600"
                        onClick={() => navigate('/manager/enrollments')}
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        }
                    />

                    <QuickActionCard
                        title={t('pages.managerDashboard.manageLevels')}
                        description={t('pages.managerDashboard.manageLevelsDesc')}
                        color="from-orange-500 to-orange-600"
                        onClick={() => navigate('/manager/levels')}
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        }
                    />

                    <QuickActionCard
                        title={t('pages.managerDashboard.sessionStatement')}
                        description={t('pages.managerDashboard.sessionStatementDesc')}
                        color="from-pink-500 to-pink-600"
                        onClick={() => navigate('/manager/reports/session-statement')}
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        }
                    />

                    <QuickActionCard
                        title={t('pages.managerDashboard.batchPayments')}
                        description={t('pages.managerDashboard.batchPaymentsDesc')}
                        color="from-cyan-500 to-cyan-600"
                        onClick={() => navigate('/manager/reports/session-attendance')}
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default ManagerDashboard;
